import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, Loader2, ArrowUp, ArrowDown } from "lucide-react";
import { fetchProducts, createProduct, updateProduct, deleteProduct, img } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import ImageUpload from "../ImageUpload";
import { toast } from "../../hooks/use-toast";

const blank = { id: "", category: "Collections", name: "", priceLabel: "", priceFrom: 0, badge: "", badgeType: "new", stock: "In Stock", delivery: "Delivered instantly", image: "", images: [], description: "", options: [{ label: "", price: 0 }], order: 99 };
const input = "w-full bg-navy-700 border border-white/15 focus:border-gold rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 outline-none transition-colors";
const label = "block text-xs text-slate-400 mb-1.5";

const ProductsPanel = () => {
  const { reload } = useSite();
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => fetchProducts().then(setProducts).catch(() => {});
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing({ ...blank }); setIsNew(true); };
  const startEdit = (p) => { setEditing(JSON.parse(JSON.stringify(p))); setIsNew(false); };
  const cancel = () => { setEditing(null); setIsNew(false); };
  const setField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const addImage = (url) => setField("images", [...(editing.images || []), url]);
  const removeImage = (i) => setField("images", editing.images.filter((_, x) => x !== i));
  const moveImage = (i, dir) => {
    const imgs = [...editing.images];
    const j = i + dir;
    if (j < 0 || j >= imgs.length) return;
    [imgs[i], imgs[j]] = [imgs[j], imgs[i]];
    setField("images", imgs);
  };

  const save = async () => {
    if (!editing.id || !editing.name) { toast({ title: "Missing fields", description: "ID and name are required." }); return; }
    const payload = {
      ...editing,
      priceFrom: Number(editing.priceFrom) || 0,
      order: Number(editing.order) || 99,
      images: (editing.images || []).filter(Boolean),
      image: (editing.images || []).filter(Boolean)[0] || editing.image,
      options: editing.options.filter((o) => o.label).map((o) => ({ label: o.label, price: Number(o.price) || 0 })),
    };
    setSaving(true);
    try {
      if (isNew) await createProduct(payload); else await updateProduct(editing.id, payload);
      toast({ title: "Saved", description: `${payload.name} saved.` });
      cancel(); await load(); await reload();
    } catch (err) { toast({ title: "Error", description: err?.response?.data?.detail || "Could not save product." }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => { if (!window.confirm("Delete this product?")) return; await deleteProduct(id); await load(); await reload(); toast({ title: "Deleted" }); };

  if (editing) {
    return (
      <div className="bg-navy-800 rounded-2xl border border-white/10 p-6">
        <div className="flex items-center justify-between mb-6"><h3 className="text-white font-bold text-lg">{isNew ? "New Product" : "Edit Product"}</h3><button onClick={cancel} className="text-slate-400 hover:text-white"><X size={20} /></button></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={label}>Product ID (slug)</label><input className={input} value={editing.id} disabled={!isNew} onChange={(e) => setField("id", e.target.value.toLowerCase().replace(/\s+/g, "-"))} placeholder="e.g. 20s-collection" /></div>
          <div><label className={label}>Name</label><input className={input} value={editing.name} onChange={(e) => setField("name", e.target.value)} /></div>
          <div><label className={label}>Category</label><select className={input} value={editing.category} onChange={(e) => setField("category", e.target.value)}><option>Collections</option><option>Mixed Bundles</option><option>Digital Guides</option></select></div>
          <div><label className={label}>Price Label</label><input className={input} value={editing.priceLabel} onChange={(e) => setField("priceLabel", e.target.value)} placeholder="$100.00 – $500.00" /></div>
          <div><label className={label}>Badge (text)</label><input className={input} value={editing.badge} onChange={(e) => setField("badge", e.target.value)} placeholder="Best Seller" /></div>
          <div><label className={label}>Badge Type</label><select className={input} value={editing.badgeType} onChange={(e) => setField("badgeType", e.target.value)}><option value="new">new (green)</option><option value="popular">popular (blue)</option><option value="best">best (gold)</option></select></div>
          <div><label className={label}>Stock text</label><input className={input} value={editing.stock} onChange={(e) => setField("stock", e.target.value)} /></div>
          <div><label className={label}>Display order</label><input className={input} type="number" value={editing.order} onChange={(e) => setField("order", e.target.value)} /></div>
        </div>
        <div className="mt-4"><label className={label}>Description</label><textarea className={`${input} h-24 resize-none`} value={editing.description} onChange={(e) => setField("description", e.target.value)} /></div>

        <div className="mt-5">
          <div className="flex items-center justify-between mb-2"><label className={label}>Product Images (shown as a slider — drag order with arrows)</label><ImageUpload onUpload={addImage} label="Upload image" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {(editing.images || []).map((src, i) => (
              <div key={i} className="relative group rounded-xl overflow-hidden border border-white/10">
                <img src={img(src)} alt="" className="w-full h-24 object-cover" />
                {i === 0 && <span className="absolute top-1 left-1 text-[10px] bg-gold text-navy px-1.5 py-0.5 rounded font-semibold">Main</span>}
                <div className="absolute inset-x-0 bottom-0 flex justify-between bg-navy/80 p-1">
                  <button onClick={() => moveImage(i, -1)} className="text-white hover:text-gold"><ArrowUp size={14} /></button>
                  <button onClick={() => moveImage(i, 1)} className="text-white hover:text-gold"><ArrowDown size={14} /></button>
                  <button onClick={() => removeImage(i)} className="text-white hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
          <input className={`${input} mt-3`} placeholder="or paste an image URL and press Enter" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (e.target.value.trim()) { addImage(e.target.value.trim()); e.target.value = ""; } } }} />
        </div>

        <div className="mt-5">
          <label className={label}>Packs / Options</label>
          <div className="space-y-2">
            {editing.options.map((o, i) => (
              <div key={i} className="flex gap-2">
                <input className={input} placeholder="Label e.g. $100 Pack" value={o.label} onChange={(e) => { const opts = [...editing.options]; opts[i] = { ...opts[i], label: e.target.value }; setField("options", opts); }} />
                <input className={`${input} w-32`} type="number" placeholder="Price" value={o.price} onChange={(e) => { const opts = [...editing.options]; opts[i] = { ...opts[i], price: e.target.value }; setField("options", opts); }} />
                <button onClick={() => setField("options", editing.options.filter((_, x) => x !== i))} className="text-slate-400 hover:text-red-400 px-2"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
          <button onClick={() => setField("options", [...editing.options, { label: "", price: 0 }])} className="mt-2 text-sm text-gold hover:underline">+ Add pack</button>
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-navy font-semibold px-5 py-2.5 rounded-xl transition-colors disabled:opacity-60">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save</button>
          <button onClick={cancel} className="px-5 py-2.5 rounded-xl border border-white/15 text-slate-300 hover:text-white transition-colors">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6"><h3 className="text-white font-bold text-lg">Products ({products.length})</h3><button onClick={startNew} className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-navy font-semibold px-4 py-2.5 rounded-xl transition-colors"><Plus size={16} /> Add Product</button></div>
      <div className="grid gap-3">
        {products.map((p) => (
          <div key={p.id} className="bg-navy-800 rounded-xl border border-white/10 p-4 flex items-center gap-4">
            <img src={img((p.images && p.images[0]) || p.image)} alt={p.name} className="h-14 w-14 rounded-lg object-cover" />
            <div className="flex-1 min-w-0"><p className="text-white font-semibold truncate">{p.name}</p><p className="text-slate-400 text-xs">{p.category} · {p.priceLabel} · {(p.images || []).length} image(s)</p></div>
            <button onClick={() => startEdit(p)} className="h-9 w-9 rounded-lg border border-gold/30 text-white flex items-center justify-center hover:bg-navy-700 transition-colors"><Pencil size={15} /></button>
            <button onClick={() => remove(p.id)} className="h-9 w-9 rounded-lg border border-white/15 text-slate-400 flex items-center justify-center hover:text-red-400 hover:border-red-400/40 transition-colors"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductsPanel;
